import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import { Partner, TradingOffer } from 'types';
import { TradingOfferType, TradingOffersSummary } from 'types/TradingOffer';
import {
  EquityMarketContextType,
  MarketInfo,
  PartnerActiveOffers,
} from '../types/EquityMarketContextType';
import { equityMarketInitValue } from '../constants/EquityMarketInitValue';
import { useGetTradingOffers } from '../hooks/useGetTradingOffers';
import HowItWorksModal from '../modals/HowItWorksModal';

export const EquityMarketContext = createContext<EquityMarketContextType>(
  equityMarketInitValue
);

const TRADING_OFFERS_PAGE_SIZE = 100;
const TRADING_OFFERS_FILTERS: string[] = [];

export const EquityMarketProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId, partnerId } = useParams<{
    societyId: string;
    partnerId: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const user = useSelector(({ user }: State) => user);
  const tradingOffers = useSelector(
    ({ tradingOffers }: State) => (tradingOffers ?? []) as TradingOffer[]
  );

  const { partnerShares, referenceInfo } = equityMarketInitValue.data;
  const recentActivity = useMemo(() => [], []);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { isLoading: isTradingOffersLoading } = useGetTradingOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  const isAdmin = useMemo(() => {
    const role = actualSociety?.administrators?.find(
      (admin: Record<string, string>) => admin?.user === user?.['_id']
    );
    return !!role;
  }, [actualSociety, user]);

  const isReadOnly = false;

  const sellOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.SELL),
    [tradingOffers]
  );

  const buyOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.BUY),
    [tradingOffers]
  );

  const calculateSummary = useCallback(
    (offers: TradingOffer[]): TradingOffersSummary => {
      const offersCount = offers.length;
      const sharesCount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total,
        0
      );
      const totalAmount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      );

      return { offersCount, sharesCount, totalAmount };
    },
    []
  );

  const sellOffersSummary = useMemo(
    () => calculateSummary(sellOffers),
    [sellOffers, calculateSummary]
  );

  const buyOffersSummary = useMemo(
    () => calculateSummary(buyOffers),
    [buyOffers, calculateSummary]
  );

  const marketInfo: MarketInfo = useMemo(() => {
    if (!sellOffers && !buyOffers) return equityMarketInitValue.data.marketInfo;

    const totalOffers = (sellOffers?.length ?? 0) + (buyOffers?.length ?? 0);
    const sellCount = sellOffers?.length ?? 0;
    const buyCount = buyOffers?.length ?? 0;
    const sellAmount = sellOffersSummary?.totalAmount ?? 0;
    const buyAmount = buyOffersSummary?.totalAmount ?? 0;

    return { totalOffers, sellCount, buyCount, sellAmount, buyAmount };
  }, [sellOffers, buyOffers, sellOffersSummary, buyOffersSummary]);

  const partnerActiveOffers: PartnerActiveOffers = useMemo(() => {
    if (!sellOffers && !buyOffers)
      return equityMarketInitValue.data.partnerActiveOffers;

    const filteredSellOffers = partnerId
      ? sellOffers?.filter(
          (offer) => (offer.partner as Partner)._id === partnerId
        )
      : sellOffers;

    const filteredBuyOffers = partnerId
      ? buyOffers?.filter(
          (offer) => (offer.partner as Partner)._id === partnerId
        )
      : buyOffers;

    const total =
      (filteredSellOffers?.length ?? 0) + (filteredBuyOffers?.length ?? 0);
    const sellCount = filteredSellOffers?.length ?? 0;
    const buyCount = filteredBuyOffers?.length ?? 0;

    const sellAmount =
      filteredSellOffers?.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      ) ?? 0;
    const buyAmount =
      filteredBuyOffers?.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      ) ?? 0;

    return { total, sellCount, buyCount, sellAmount, buyAmount };
  }, [sellOffers, buyOffers, partnerId]);

  const handleCreateSellOffer = useCallback(() => {
    history.push(`/crear-oferta-venta/${societyId}`);
  }, [history, societyId]);

  const handleCreateBuyOffer = useCallback(() => {
    history.push(`/crear-oferta-compra/${societyId}`);
  }, [history, societyId]);

  const handleViewSellOffers = useCallback(() => {
    history.push(`/ofertas-venta/${societyId}`);
  }, [history, societyId]);

  const handleViewBuyOffers = useCallback(() => {
    history.push(`/ofertas-compra/${societyId}`);
  }, [history, societyId]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta/${societyId}/${offerId}`);
    },
    [history, societyId]
  );

  const handleViewMyShares = useCallback(() => {
    history.push(`/mis-acciones/${societyId}`);
  }, [history, societyId]);

  const handleViewMyOffers = useCallback(() => {
    history.push(`/ofertas/${societyId}/${user._id}`);
  }, [history, societyId, user]);

  const handleOpenHowItWorks = useCallback(() => {
    dispatch(setModal(<HowItWorksModal size={sizes.LG} />));
  }, [dispatch]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        partnerShares,
        partnerActiveOffers,
        marketInfo,
        sellOffers,
        buyOffers,
        sellOffersSummary,
        buyOffersSummary,
        recentActivity,
        referenceInfo,
        isAdmin,
        isReadOnly,
        isLoading: isTradingOffersLoading || !actualSociety,
      },
      actions: {
        handleCreateSellOffer,
        handleCreateBuyOffer,
        handleViewSellOffers,
        handleViewBuyOffers,
        handleViewOfferDetail,
        handleViewMyShares,
        handleViewMyOffers,
        handleOpenHowItWorks,
      },
    }),
    [
      societyId,
      actualSociety,
      partnerShares,
      partnerActiveOffers,
      marketInfo,
      sellOffers,
      buyOffers,
      sellOffersSummary,
      buyOffersSummary,
      recentActivity,
      referenceInfo,
      isAdmin,
      isReadOnly,
      isTradingOffersLoading,
      handleCreateSellOffer,
      handleCreateBuyOffer,
      handleViewSellOffers,
      handleViewBuyOffers,
      handleViewOfferDetail,
      handleViewMyShares,
      handleViewMyOffers,
      handleOpenHowItWorks,
    ]
  );

  return (
    <EquityMarketContext.Provider value={contextValue}>
      {children}
    </EquityMarketContext.Provider>
  );
};

export const useEquityMarket = () => {
  const context = useContext(EquityMarketContext);
  if (!context) {
    throw new Error(
      'useEquityMarket must be used within an EquityMarketProvider'
    );
  }
  return context;
};
